import React from 'react';
import {
    Layout,
    Page,
    PageActions,
} from '@shopify/polaris';
import { Helmet } from 'react-helmet';
import { createTagAction, validateTagAction } from '../../../../common/TagActions';
import ResourceConditionsPaneComponent from '../../../../components/filtering/ResourceConditionsPaneComponent';
import ResourceTagActionsPaneComponent from '../../../../components/filtering/ResourceTagActionsPaneComponent';
import ResourcePreviewPaneComponent from '../../../../components/filtering/ResourcePreviewPaneComponent';
import OperationStepCardComponent from '../../../../components/filtering/OperationStepCardComponent';
import OperationExecutionOptionsPane from '../../../../components/filtering/OperationExecutionOptionsPane';
import OperationAutomationOptionsPane from '../../../../components/filtering/OperationAutomationOptionsPane';
import OperationStepHeader from './OperationStepHeader';
import { UIAttributeFilter, UICustomerAddressesFilter } from '../../../../common/UICustomerFilter';

function HasConditionErrors(conditions) {
    const withErrors = conditions.filter(_ => _.error != null);

    return withErrors.length > 0;
}

class OrderOperationsCreateStep extends React.Component {

    nextTagActionId = 0;

    constructor(props) {
        super(props)

        this.state = {
            tagActions: [],
            filters: [],
            filtersLogicalOperator: "And",
            description: null,
            preview: null,
            executing: false,
            editing: false,
            execution: {}
        }

        if (props.operation != null) {
            this.state.tagActions = props.operation.actions;
            this.state.filters = props.operation.filters.map((_, index) => {

                if (_.filterType === "AttributeFilter") {
                    return UIAttributeFilter.fromBody(index + 1, _);
                }

                if (_.filterType === "CustomerAddressesFilter") {
                    return UICustomerAddressesFilter.fromBody(index + 1, _);
                }

                throw new Error("Unknown filter type");
            });

            this.state.filtersLogicalOperator = props.operation.filtersLogicalOperator;
            this.state.description = props.operation.description;
            this.state.id = props.operation.id;
            this.state.editing = true;
        }

        if (props.execution != null) {
            this.state.execution.executionChoice = props.execution.executionChoice;
            this.state.execution.executionTime = props.execution.executionTime;
            this.state.execution.executionFrequency = props.execution.executionFrequency;
        }

        this.nextTagActionId = this.getLargestId(this.state.tagActions);
        this.state.tagActions.forEach(_ => validateTagAction(_));
    }

    componentDidUpdate(prevProps) {

        if (this.props.operation !== prevProps.operation && this.props.operation != null) {
            this.setState({
                tagActions: this.props.operation.actions,
                filters: this.props.operation.filters,
                filtersLogicalOperator: this.props.operation.filtersLogicalOperator,
                description: this.props.operation.description,
                editing: true,
            });
        }
    }

    getLargestId = (items) => {
        if (items.length === 0) {
            return 0;
        }

        const max = items.reduce(function (max, item) {
            return Math.max(max, item.id);
        }, 0);
        return max + 1;
    }

    render() {

        let hasActions = this.state.tagActions.length > 0;
        let hasConditions = this.state.filters.length > 0;
        let hasActionErrors = this.state.tagActions.filter(_ => _.error != null).length > 0;
        let hasConditionErrors = HasConditionErrors(this.state.filters);
        let canProceed = hasActions && !hasConditionErrors && !hasActionErrors;
        let canPreview = hasActions && !hasConditionErrors && !hasActionErrors;

        let secondaryActions = [];
        let primaryAction = null;
        let title = "Create Order Tag Operation"
        let pageActions = null;

        if (this.state.editing) {
            primaryAction = { content: "Save changes", disabled: !canPreview, onAction: this.onSave };
            secondaryActions.push({ content: "Cancel", onAction: this.props.onCancel });
            title = "Edit Order Tag Operation";
            pageActions = <PageActions
                primaryAction={primaryAction}
                secondaryActions={secondaryActions}
            />
        }

        return (
            <div>
                <Helmet>
                    <title>Tag Genie - {title}</title>
                </Helmet>
                <Page
                    title={title}
                    subtitle={this.state.description}
                    primaryAction={primaryAction}
                    secondaryActions={secondaryActions}>
                    <Layout sectioned>
                        <OperationStepCardComponent
                            title={<OperationStepHeader text="SELECT ORDERS TO EDIT" number="1" />}
                            showDescription={!hasConditions}
                            description="Use conditions to specify which orders will be considered for this operation.">
                            <ResourceConditionsPaneComponent
                                resourceType="Order"
                                resourceSingular="order"
                                resourcePlural="orders"
                                autoUpdateFilters={true}
                                expanded={true}
                                tags={this.props.tags}
                                filters={this.state.filters}
                                filtersLogicalOperator={this.state.filtersLogicalOperator}
                                onFiltersUpdated={this.handleFiltersUpdated}
                            />
                        </OperationStepCardComponent>
                        <OperationStepCardComponent
                            title={<OperationStepHeader text="CHOOSE HOW TO EDIT TAGS" number="2" />}
                            showDescription={!hasActions}
                            description="Use tag actions to add or remove tags. Multiple actions can be specified. For example, you can remove multiple tags and add multiple tags at the same time.">
                            <ResourceTagActionsPaneComponent
                                tagActions={this.state.tagActions}
                                tags={this.props.tags}
                                onCreateTagAction={this.handleOnCreateTagAction}
                                onDeleteTagAction={this.handleOnDeleteTagAction}
                                onUpdateTagAction={this.handleOnUpdateTagAction}
                            />
                        </OperationStepCardComponent>
                        <OperationStepCardComponent
                            title={<OperationStepHeader text="PREVIEW CHANGES" number="3" />}
                            disabled={!canProceed}
                            showDescription={this.props.preview == null}
                            description="Preview which orders will be affected by this operation and how."
                            expanded={true}>
                            <ResourcePreviewPaneComponent
                                resourcePlural="orders"
                                resourceSingular="order"
                                onPreview={this.handleOnPreview}
                                disabled={!canProceed}
                                loadingPreview={this.props.loadingPreview}
                                preview={this.props.preview} />
                        </OperationStepCardComponent>
                        {!this.state.editing && <OperationStepCardComponent
                            disabled={!canProceed}
                            title={<OperationStepHeader text="EXECUTE" number="4" />}
                            description="Execute operation or schedule it for the future">
                            <OperationExecutionOptionsPane
                                execution={this.state.execution}
                                disabled={!canProceed}
                                loading={this.state.executing}
                                onUpdateExecution={this.handleUpdateExecution}
                                onExecute={this.onExecute} />
                        </OperationStepCardComponent>
                        }
                        {this.state.editing && <OperationStepCardComponent
                            disabled={!canProceed}
                            title={<OperationStepHeader text="AUTOMATE" number="4" />}
                            description="Enable operation to be executed on a schedule or execute manually.">
                            <OperationAutomationOptionsPane
                                execution={this.state.execution}
                                disabled={!canProceed}
                                loading={this.state.executing}
                                onUpdateExecution={this.handleUpdateExecution} />
                        </OperationStepCardComponent>
                        }
                    </Layout>
                    {pageActions}
                </Page>
            </div >

        );
    }

    handleOnPreview = () => {
        this.props.onPreview(this.state.tagActions, this.state.filters, this.state.filtersLogicalOperator);
    }

    handleUpdateExecution = (newValue) => {
        this.setState({
            "execution": newValue,
        });
    }

    handleDescriptionChange = (newValue) => {
        this.setState({
            "description": newValue,
        });
    }

    handleFiltersUpdated = (newFilters, productFiltersLogicalOperator) => {
        this.setState({
            "filters": newFilters,
            "filtersLogicalOperator": productFiltersLogicalOperator,
        });
        this.props.onClearPreview()
    }

    handleOnCreateTagAction = (actionType) => {

        let newAction = null;

        if (actionType === "add") {
            newAction = createTagAction(this.nextTagActionId++, "add");
        }

        if (actionType === "changePrefix") {
            newAction = createTagAction(this.nextTagActionId++, "changePrefix");
        }

        if (actionType === "conditionalAdd") {
            newAction = createTagAction(this.nextTagActionId++, "conditionalAdd");
        }

        if (actionType === "remove") {
            newAction = createTagAction(this.nextTagActionId++, "remove");
        }

        if (actionType === "rename") {
            newAction = createTagAction(this.nextTagActionId++, "rename");
        }

        let newTagActions = [
            ...this.state.tagActions,
            newAction
        ];

        this.setState({
            tagActions: newTagActions,
        });
        this.props.onClearPreview();
    }

    getTagActionById = (id) => {
        var matching = this.state.tagActions.filter((item) => item.id === id);

        if (matching.length > 0) {
            return matching[0];
        }

        return null;
    }

    handleOnUpdateTagAction = (updatedAction) => {

        let tagAction = this.getTagActionById(updatedAction.id);

        if (tagAction == null) {
            return;
        }

        if (tagAction.type !== updatedAction.type) {
            updatedAction = this.initNewTagAction(updatedAction.id, updatedAction.type);
        }

        validateTagAction(updatedAction);
        let index = this.state.tagActions.findIndex(item => item.id === tagAction.id);
        let newTagActions = [...this.state.tagActions];
        newTagActions[index] = updatedAction;
        this.setState({
            tagActions: newTagActions,
        });
        this.props.onClearPreview();
    }

    onExecute = (execution) => {
        this.props.onSubmit({
            actions: this.state.tagActions,
            filters: this.state.filters,
            filtersLogicalOperator: this.state.filtersLogicalOperator,
            execution: execution
        });
    }

    onSave = () => {
        this.props.onSave({
            actions: this.state.tagActions,
            filters: this.state.filters,
            filtersLogicalOperator: this.state.filtersLogicalOperator,
            execution: this.state.execution
        });
    }

    handleOnDeleteTagAction = (index) => {
        let newTagActions = [
            ...this.state.tagActions.slice(0, index),
            ...this.state.tagActions.slice(index + 1)
        ];

        this.setState({
            tagActions: newTagActions,
            "preview": null
        });
    }

    componentDidMount() {
    }
}

OrderOperationsCreateStep.defaultProps = {
    operation: null,
}

export default OrderOperationsCreateStep;